import React, {
  ChangeEvent,
  Fragment,
  MouseEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { useNavigate } from "react-router";
import { useSelector } from "react-redux";

import { currentUser } from "store/ducks/user";
import { RootState } from "store/store";
import {
  dispatchSetUser,
  dispatchSetSelectedCountry,
  dispatchSetSelectedCountryLoading,
} from "store/dispatch/user.dispatch";

import FormButton from "FormElement/FormButton";
import HeaderBreadcrumb from "icons/HeaderBreadcrumb";
import ArrowDownIcon from "icons/ArrowDownIcon";
import EditIcon from "icons/EditIcon";
import CheckMarkIcon from "icons/Checkmark";
import InputField from "FormElement/InputField";
import {
  headerHandleChange,
  headerSubmitHandler,
  headerValidationSchema,
} from "./utils/utils";
import { Form, Formik } from "formik";
import { UpdateNameRequest } from "./services/services";
import { ToastContainer } from "react-toastify";
import { axiosGet } from "../axios/axios-config";
import {
  COUNTRY_BITS_SOLD_EVENT,
  CountryBitsSoldDetail,
} from "utils/countryBitsEvents";
import Closemodal from "icons/Closemodal";
import { Bit } from "types/types";

interface HeaderProps {
  onNavButtonClick: () => void;
  setHideBanner: Function;
  hideBanner: boolean;
  loggedIn: boolean;
  setLogedIn: Function;
  setLogoutUser: Function;
  loggedUserPurchasedBitInfo: null | Bit[];
  setProfileInfo: Function;
  setGiftBitPopup: Function;
}

const Header: React.FC<HeaderProps> = ({
  onNavButtonClick,
  setHideBanner,
  hideBanner,
  loggedIn,
  setLogedIn,
  setLogoutUser,
  loggedUserPurchasedBitInfo,
  setProfileInfo,
  setGiftBitPopup,
}) => {
  const [editUserName, setEditUserName] = useState<boolean>(false);
  const [fullName, setFullName] = useState<null | string>(null);
  const user = useSelector(currentUser);
  const countryInfo = useSelector((state: RootState) => state.countryInfo);
  const selectedCountryState = useSelector(
    (state: RootState) => state.selectedCountry,
  );
  const [countries, setCountries] = useState<
    {
      id: number;
      code: string;
      name: string;
      flag_image: string;
      price_per_bit: number;
      currency_name: string | null;
      currency_sort_name: string | null;
      currency_symbol: string | null;
      bits_taken?: number;
    }[]
  >([]);
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>(
    countryInfo?.country_code || "NZ",
  );
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] =
    useState<boolean>(false);
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  const countryToggleRef = useRef<HTMLButtonElement>(null);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const hasFetchedCountries = useRef<boolean>(false);
  const hasAppliedIPCountry = useRef<boolean>(false);

  const refreshAllowedCountries = useCallback(async () => {
    try {
      const res: any = await axiosGet("/auth/allowed-countrys/");
      if (res?.data) {
        setCountries(res.data);
      }
    } catch {
      /* ignore — dropdown / header still shows last known data */
    }
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;

      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        if (toggleRef.current && !toggleRef.current.contains(target)) {
          setIsVisible(false);
        }
      }

      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(target)
      ) {
        if (
          countryToggleRef.current &&
          !countryToggleRef.current.contains(target)
        ) {
          setIsCountryDropdownOpen(false);
        }
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const onSold = (event: Event) => {
      const detail = (event as CustomEvent<CountryBitsSoldDetail>).detail;
      if (
        detail == null ||
        typeof detail.countryId !== "number" ||
        typeof detail.coordinateCount !== "number" ||
        detail.coordinateCount <= 0
      ) {
        return;
      }
      setCountries((prev) =>
        prev.map((c) =>
          c.id === detail.countryId
            ? { ...c, bits_taken: (c.bits_taken ?? 0) + detail.coordinateCount }
            : c,
        ),
      );
    };
    window.addEventListener(COUNTRY_BITS_SOLD_EVENT, onSold);
    return () => window.removeEventListener(COUNTRY_BITS_SOLD_EVENT, onSold);
  }, []);

  useEffect(() => {
    if (!isCountryDropdownOpen) {
      return;
    }
    void refreshAllowedCountries();
    const intervalId = window.setInterval(() => {
      void refreshAllowedCountries();
    }, 20000);
    return () => window.clearInterval(intervalId);
  }, [isCountryDropdownOpen, refreshAllowedCountries]);

  useEffect(() => {
    if (user.first_name && user.last_name) {
      setFullName(user.first_name + " " + user.last_name);
    }
    const handleScroll = () => {
      if (window.scrollY > 100) {
        document.body.classList.add("scrolled");
      } else {
        document.body.classList.remove("scrolled");
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.body.classList.remove("scrolled"); // Cleanup on unmount or user change
    };
  }, [user]);

  useEffect(() => {
    // Prevent multiple API calls
    if (hasFetchedCountries.current) {
      return;
    }

    const fetchCountries = async () => {
      try {
        hasFetchedCountries.current = true;
        dispatchSetSelectedCountryLoading(true);
        const res: any = await axiosGet("/auth/allowed-countrys/");
        if (res && res.data) {
          setCountries(res.data);
          // Prefer manually/persisted selected country; fallback to IP country, then first list item.
          const codeFromSelectedCountry = selectedCountryState.country?.code;
          const codeFromIPCountry = countryInfo?.country_code;
          let selectedCode = codeFromSelectedCountry || codeFromIPCountry;
          if (!selectedCode && res.data.length > 0) {
            selectedCode = res.data[0].code;
          }
          if (selectedCode) {
            setSelectedCountryCode(selectedCode);
            const foundCountry = res.data.find(
              (c: any) => c.code === selectedCode,
            );
            if (foundCountry) {
              dispatchSetSelectedCountry({
                id: foundCountry.id,
                code: foundCountry.code,
                name: foundCountry.name,
                flag_image: foundCountry.flag_image,
                price_per_bit: foundCountry.price_per_bit,
                currency_name: foundCountry.currency_name || "Dollar",
                currency_sort_name: foundCountry.currency_sort_name || null,
                currency_symbol: foundCountry.currency_symbol || null,
              });
            }
          }
        }
      } catch (e) {
        // Silently fail – header will just show default
        hasFetchedCountries.current = false; // Reset on error to allow retry
      } finally {
        dispatchSetSelectedCountryLoading(false);
      }
    };

    fetchCountries();
  }, [countryInfo?.country_code]);

  useEffect(() => {
    const codeFromIP = countryInfo?.country_code;
    if (!codeFromIP || countries.length === 0 || hasAppliedIPCountry.current) {
      return;
    }

    // If a real country is already selected (manual/persisted), do not override it with IP lookup.
    if (
      selectedCountryState.country?.id &&
      selectedCountryState.country.id !== 0
    ) {
      hasAppliedIPCountry.current = true;
      return;
    }

    const foundCountry = countries.find(
      (country) => country.code === codeFromIP,
    );
    if (!foundCountry) return;

    if (selectedCountryState.country?.code !== foundCountry.code) {
      setSelectedCountryCode(foundCountry.code);
      dispatchSetSelectedCountry({
        id: foundCountry.id,
        code: foundCountry.code,
        name: foundCountry.name,
        flag_image: foundCountry.flag_image,
        price_per_bit: foundCountry.price_per_bit,
        currency_name: foundCountry.currency_name || "Dollar",
        currency_sort_name: foundCountry.currency_sort_name || null,
        currency_symbol: foundCountry.currency_symbol || null,
      });
    }

    hasAppliedIPCountry.current = true;
  }, [countryInfo?.country_code, countries, selectedCountryState.country?.id]);

  // Use Redux selectedCountry if available, otherwise fallback to local computation
  const selectedCountry = selectedCountryState.country || {
    code: countryInfo?.country_code || "NZ",
    name: countryInfo?.country_name || "New Zealand",
    flag_image: "images/flags/nz.png",
    price_per_bit: 1,
    currency_name: countryInfo?.currency_name || "Dollar",
    currency_sort_name: null,
    currency_symbol: "$",
    id: 0,
  };

  // Combined loading state: show loader if either countryInfo or selectedCountry is loading
  const isLoading = countryInfo?.isLoading || selectedCountryState.isLoading;

  const toggleVisibility = () => {
    setIsVisible((prev) => {
      return !prev;
    });
  };

  return (
    <div className="header-wrap">
      <header
        className={`header py-18px md:py-2.5 fixed w-full border-b-0 border-white/20 top-0 z-50 transition-colors duration-500 ${hideBanner ? "bg-[#041019] shadow-lg" : "bg-transparent"}`}
      >
        <div className="container">
          <div className="relative header-nav flex items-center justify-between gap-2 sm:gap-0">
            <div className="flex items-center gap-2">
              <button
                onClick={onNavButtonClick}
                title="Site navigation"
                className="bg-borderGradient group overflow-hidden relative rounded-full   text-[20px] leading-none cursor-pointer font-normal text-white md:px-[34px] md:h-auto h-10 md:w-auto md:block flex items-center justify-center w-10 md:py-[15px] hover:opacity-80 duration-300 transition-all"
              >
                <span className="relative z-10 flex gap-3 items-center">
                  {/* <span className="group-hover:scale-[150%] transition-all duration-300"> */}
                  {/* <HeaderBreadcrumb /> */}
                  <svg
                    className="w-7 h-7 group-hover:scale-[150%] transition-all duration-300"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                    <g
                      id="SVGRepo_tracerCarrier"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></g>
                    <g id="SVGRepo_iconCarrier">
                      {" "}
                      <path
                        d="M4 18L20 18"
                        stroke="#ffffff"
                        strokeWidth="2"
                        strokeLinecap="round"
                      ></path>{" "}
                      <path
                        d="M4 12L20 12"
                        stroke="#ffffff"
                        strokeWidth="2"
                        strokeLinecap="round"
                      ></path>{" "}
                      <path
                        d="M4 6L20 6"
                        stroke="#ffffff"
                        strokeWidth="2"
                        strokeLinecap="round"
                      ></path>{" "}
                    </g>
                  </svg>
                  {/* </span> */}
                  <span className="md:block hidden">Info</span>
                </span>
                <span className=""></span>
              </button>
              <button
                title="Show homepage info"
                onClick={() => {
                  navigate("/");
                  const bannerSection =
                    document.getElementById("banner-section");
                  if (!bannerSection) return;
                  // Scroll first
                  window.scrollTo({
                    top: bannerSection.offsetTop,
                    behavior: "smooth",
                  });

                  // Wait ~500ms (adjust to match smooth scroll speed) before showing banner
                  setTimeout(() => {
                    setHideBanner(false);
                  }, 500);
                }}
                className={`group bg-borderGradient hover:cursor-pointer overflow-hidden relative md:rounded-[60px] rounded-full text-[40px] leading-none cursor-pointer font-normal text-white w-10 md:w-16 h-10 md:h-16 hover:opacity-80 duration-300 ${
                  hideBanner ? "visible" : "invisible"
                } flex items-center justify-center md:py-[20px]`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="size-6 relative z-10 hover:cursor-pointer mx-auto group-hover:scale-[150%] transition-all duration-300 "
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
                  />
                </svg>

                <span className="bg-btnBg_img absolute inset-[2px_2px_2px_2px] transition-all duration-300 md:group-hover:inset-[6px_6px_6px_6px]   w-[calc(100% - 2px)] rounded-[60px]"></span>
              </button>
            </div>
            {countries.length === 1 ? (
              <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 group transition-all duration-300 country-name text-center flex flex-col gap-1 justify-center items-center">
                <div className="flex items-center gap-1.5 sm:gap-2 px-2.5 py-1.5 md:py-2.5 rounded-full bg-white/10 border border-white/20">
                  <span className="relative rounded-full w-7 h-7 md:w-9 md:h-9 bg-borderGradient inline-flex items-center justify-center overflow-hidden shrink-0">
                    <img
                      src={`${process.env.REACT_APP_API_URL}/media/${selectedCountry.flag_image}`}
                      alt={selectedCountry.name}
                      className="w-6 h-6 md:w-8 md:h-8 rounded-full object-cover"
                    />
                  </span>
                  <span className="flex items-center gap-1 text-white/80 text-sm md:text-base font-jost">
                    <span className="hidden sm:inline-block">
                      {selectedCountry.name}
                    </span>
                    <span className="sm:hidden text-xs font-semibold">
                      {selectedCountry.code}
                    </span>
                  </span>
                </div>
              </div>
            ) : (
              <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 group transition-all duration-300 country-name text-center flex flex-col gap-1 justify-center items-center">
                {isLoading ? (
                  <div className="flex items-center gap-2 px-4 py-1.5 md:py-2.5 rounded-full bg-white/10 border border-white/20">
                    <div className="w-7 h-7 md:w-9 md:h-9 rounded-full border-2 border-white/30 border-t-white animate-spin"></div>
                    <span className="text-white/60 text-sm md:text-base font-jost">
                      Loading...
                    </span>
                  </div>
                ) : (
                  <button
                    ref={countryToggleRef}
                    type="button"
                    onClick={() =>
                      setIsCountryDropdownOpen((prevOpen) => !prevOpen)
                    }
                    className="flex items-center gap-1.5 sm:gap-2 px-2.5 py-1.5 md:py-2.5 rounded-full bg-white/10 border border-white/20 hover:bg-white/20 transition-all duration-200"
                  >
                    <span className="relative rounded-full w-7 h-7 md:w-9 md:h-9 bg-borderGradient inline-flex items-center justify-center overflow-hidden shrink-0">
                      <img
                        src={`${process.env.REACT_APP_API_URL}/media/${selectedCountry.flag_image}`}
                        alt={selectedCountry.name}
                        className="w-6 h-6 md:w-8 md:h-8 rounded-full object-cover"
                      />
                    </span>
                    <span className="flex items-center gap-1 text-white/80 text-sm md:text-base font-jost">
                      <span className="hidden sm:inline-block">
                        {selectedCountry.name}
                      </span>
                      <span className="sm:hidden text-xs font-semibold">
                        {selectedCountry.code}
                      </span>
                      <span className="inline-flex ml-1">
                        <ArrowDownIcon />
                      </span>
                    </span>
                  </button>
                )}

                {isCountryDropdownOpen && (
                  <div
                    ref={countryDropdownRef}
                    className="absolute left-1/2 -translate-x-1/2 mt-2 top-full w-[min(26rem,calc(100vw-1.5rem))] min-w-[18rem] max-h-72 overflow-y-auto rounded-lg border border-white/15 bg-[#041019]/95 backdrop-blur-lg shadow-xl text-left z-50"
                  >
                    <div className="sticky top-0 z-10 grid grid-cols-[1fr_auto] gap-2 items-center px-3 py-2 border-b border-white/10 bg-[#041019]/98 backdrop-blur-sm">
                      <span />
                      <span className="text-[10px] font-medium text-white/55 tracking-wide text-right whitespace-nowrap">
                        Bits Taken
                      </span>
                    </div>
                    {countries.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => {
                          setSelectedCountryCode(c.code);
                          dispatchSetSelectedCountry({
                            id: c.id,
                            code: c.code,
                            name: c.name,
                            flag_image: c.flag_image,
                            price_per_bit: c.price_per_bit,
                            currency_name: c.currency_name || "Dollar",
                            currency_sort_name: c.currency_sort_name || null,
                            currency_symbol: c.currency_symbol || null,
                          });
                          setIsCountryDropdownOpen(false);
                        }}
                        className={`w-full grid grid-cols-[1fr_auto] gap-2 items-center px-3 py-2.5 text-sm text-white/80 hover:bg-white/10 transition-colors text-left ${
                          c.code === selectedCountryCode ? "bg-white/10" : ""
                        }`}
                      >
                        <span className="flex items-center gap-2 min-w-0">
                          <span className="relative rounded-full w-7 h-7 shrink-0 bg-borderGradient inline-flex items-center justify-center overflow-hidden">
                            <img
                              src={`${process.env.REACT_APP_API_URL}/media/${c.flag_image}`}
                              alt={c.name}
                              className="w-6 h-6 rounded-full object-cover"
                            />
                          </span>
                          <span className="flex flex-col items-start min-w-0">
                            <span className="font-medium truncate max-w-full">
                              {c.name}
                            </span>
                            <span className="text-[11px] text-white/50">
                              {c.code}
                              {c.currency_sort_name || "$"}{" "}
                              {(c.price_per_bit || 1).toFixed(0)} per Pixel
                            </span>
                          </span>
                        </span>
                        <span className="text-sm font-semibold tabular-nums text-white/90 shrink-0 justify-self-end min-w-[2.5rem] text-right">
                          {(c.bits_taken ?? 0).toLocaleString()}
                        </span>
                      </button>
                    ))}
                    {countries.length === 0 && (
                      <div className="px-3 py-2 text-xs text-white/60">
                        Countries not available
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="w-auto">
              <FormButton
                value="Sign Up"
                className={` font-jost md:px-8 md:py-[22px] py-3 px-3.5 rounded-full ${
                  loggedIn ? "hidden" : ""
                }`}
                onClick={() => {
                  navigate("/register");
                }}
              />
              <div className={`relative ${loggedIn ? "" : "hidden"}`}>
                <button
                  title="My Profile"
                  ref={toggleRef}
                  onClick={toggleVisibility}
                  className="text-white group flex gap-2.5 items-center py-2 px-2  md:py-4 md:px-4  lg:py-3.5 lg:px-[10px] lg:px-[18px] text-xs md:text-lg border border-white/10 bg-white/10 rounded-full  hover:opacity-80 duration-300"
                >
                  <span className="hidden lg:inline">
                    {user.first_name} {user.last_name}
                  </span>
                  <div className="inline-block group-hover:scale-[150%] transition-all duration-300">
                    <ArrowDownIcon />
                  </div>
                </button>

                {isVisible && (
                  <div
                    className="w-[200px] sm:w-[260px] max-w-[260px] top-[77px] md:top-[82px] px-[18px] pt-[30px] pb-4 text-white border border-white/20 absolute z-40 right-0  rounded-[10px] backdrop-blur-[10px] bg-menuDropBg shadow-insetMenu box-content"
                    ref={dropdownRef}
                  >
                    <div className="triangle w-6 h-6  absolute rounded-[1px] border border-white/10 hidden"></div>
                    <div className="text-xl relative font-outfit font-semibold pb-3.5 border-b border-white/20 flex justify-between items-center ">
                      <h4 className="text-xl flex gap-3 font-outfit text-white">
                        Profile
                      </h4>
                      <span
                        title="Close panel"
                        className="opacity-40 hover:opacity-100 hover:rotate-90 duration-200"
                      >
                        <Closemodal
                          className={`w-4 h-4 fill-white`}
                          setClosePopUp={setIsVisible}
                        />
                      </span>
                    </div>
                    <Formik
                      initialValues={{ full_name: fullName }}
                      validationSchema={headerValidationSchema}
                      onSubmit={(
                        values,
                        { setErrors, resetForm, setSubmitting },
                      ) => {
                        headerSubmitHandler(
                          fullName!,
                          setFullName,
                          user.email,
                          UpdateNameRequest,
                          setErrors,
                          setSubmitting,
                          dispatchSetUser,
                          user,
                        );
                      }}
                    >
                      {({ errors, touched, handleSubmit }) => (
                        <Form>
                          <div className="user-info py-4 flex justify-between  border-b border-white/20">
                            <div className="userLeft">
                              <span className="text-base min-h-7 flex mb-2 items-end">
                                {editUserName ? (
                                  <Fragment>
                                    <InputField
                                      type="text"
                                      className="bg-transparent border-0 border-b border-[#bg-transparent border-0 border-b border-[#ff0000] outline-none caret-white] outline-none caret-white"
                                      id="full_name"
                                      addDefaultClass={false}
                                      showError={false}
                                      onChange={(event) => {
                                        headerHandleChange(event, setFullName);
                                      }}
                                      value={fullName!}
                                      name="full_name"
                                    />
                                    <br />
                                  </Fragment>
                                ) : (
                                  <>
                                    {user.first_name} {user.last_name}
                                  </>
                                )}
                              </span>
                              {errors.full_name && touched.full_name ? (
                                <div className="text-red-500 text-sm mb-1">
                                  {errors.full_name}
                                </div>
                              ) : null}

                              <p className="text-sm text-white/60">
                                {user.email}
                              </p>
                            </div>
                            <div
                              onClick={() => {
                                if (editUserName) {
                                  handleSubmit(); // Submit the form when clicking CheckMarkIcon
                                }
                                if (!(errors.full_name && touched.full_name)) {
                                  setEditUserName(!editUserName);
                                }
                              }}
                              className="edit-icon transition-opacity flex items-center justify-center text-center cursor-pointer size-7 shrink-0 hover:opacity-50"
                              title="Edit your registered name"
                            >
                              {editUserName ? (
                                <CheckMarkIcon className="text-[#51C128] size-[21px]" />
                              ) : (
                                <EditIcon />
                              )}
                            </div>
                          </div>
                        </Form>
                      )}
                    </Formik>
                    <div className="myBitsBox block w-full">
                      <div className="pb-2 pt-2 text-lg sticky top-0 font-bold">
                        My Bits
                      </div>
                      <div className=" noscroll  max-h-[207px] overflow-y-auto mb-0 bg-white/10 duration-200 rounded-md relative">
                        {loggedUserPurchasedBitInfo &&
                        (loggedUserPurchasedBitInfo.length > 1 ||
                          (loggedUserPurchasedBitInfo?.length == 1 &&
                            loggedUserPurchasedBitInfo[0].bitdetail !== null &&
                            loggedUserPurchasedBitInfo[0].bitimage !==
                              null)) ? (
                          loggedUserPurchasedBitInfo?.map(
                            (purchasedBit, index) => {
                              return purchasedBit.bitdetail &&
                                purchasedBit.bitimage ? (
                                <article
                                  title="View Bit"
                                  className="group flex gap-2 w-full h-full last:mb-0 cursor-pointer rounded-md hover:bg-white/10 transition-all duration-300 px-3 py-2"
                                  key={index}
                                  onClick={() => {
                                    setProfileInfo({
                                      ownBit: true,
                                      bitIndex: purchasedBit?.id,
                                      // bitIndex: index,
                                    });
                                  }}
                                >
                                  <div className="w-11 h-11 min-w-11">
                                    {purchasedBit.bitimage.map((img, index) => {
                                      return img.starred ? (
                                        <img
                                          key={index}
                                          src={img.image}
                                          className="w-full h-full object-cover group-hover:scale-105 transition-all duration-300 rounded-[5px]"
                                          alt="Bit image"
                                          title="My Bit"
                                        />
                                      ) : (
                                        <Fragment key={index} />
                                      );
                                    })}
                                  </div>
                                  <div className="">
                                    <p className="line-clamp-1 break-all text-white text-xs sm:text-sm">
                                      {purchasedBit.bitdetail?.bit_title}
                                    </p>
                                    <p className="line-clamp-1 break-all text-white/50 text-xs font-normal mt-2">
                                      {purchasedBit.bitcoordinate.length}{" "}
                                      {purchasedBit.bitcoordinate.length === 1
                                        ? "Bit"
                                        : "Bits"}{" "}
                                      | {purchasedBit.bitcoordinate.length}00
                                      Pixels
                                    </p>
                                  </div>
                                </article>
                              ) : (
                                <Fragment key={index} />
                              );
                            },
                          )
                        ) : (
                          <div className="p-2">
                            <div className="flex gap-2 items-center">
                              <svg
                                className="text-white/50 w-4 h-4 "
                                viewBox="0 0 16 16"
                                fill="currentColor"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <g id="SVGRepo_bgCarrier" strokeWidth={0}></g>
                                <g
                                  id="SVGRepo_tracerCarrier"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                ></g>
                                <g id="SVGRepo_iconCarrier">
                                  <path
                                    d="M7.493 0.015 C 7.442 0.021,7.268 0.039,7.107 0.055 C 5.234 0.242,3.347 1.208,2.071 2.634 C 0.660 4.211,-0.057 6.168,0.009 8.253 C 0.124 11.854,2.599 14.903,6.110 15.771 C 8.169 16.280,10.433 15.917,12.227 14.791 C 14.017 13.666,15.270 11.933,15.771 9.887 C 15.943 9.186,15.983 8.829,15.983 8.000 C 15.983 7.171,15.943 6.814,15.771 6.113 C 14.979 2.878,12.315 0.498,9.000 0.064 C 8.716 0.027,7.683 -0.006,7.493 0.015 M8.853 1.563 C 9.967 1.707,11.010 2.136,11.944 2.834 C 12.273 3.080,12.920 3.727,13.166 4.056 C 13.727 4.807,14.142 5.690,14.330 6.535 C 14.544 7.500,14.544 8.500,14.330 9.465 C 13.916 11.326,12.605 12.978,10.867 13.828 C 10.239 14.135,9.591 14.336,8.880 14.444 C 8.456 14.509,7.544 14.509,7.120 14.444 C 5.172 14.148,3.528 13.085,2.493 11.451 C 2.279 11.114,1.999 10.526,1.859 10.119 C 1.618 9.422,1.514 8.781,1.514 8.000 C 1.514 6.961,1.715 6.075,2.160 5.160 C 2.500 4.462,2.846 3.980,3.413 3.413 C 3.980 2.846,4.462 2.500,5.160 2.160 C 6.313 1.599,7.567 1.397,8.853 1.563 M7.706 4.290 C 7.482 4.363,7.355 4.491,7.293 4.705 C 7.257 4.827,7.253 5.106,7.259 6.816 C 7.267 8.786,7.267 8.787,7.325 8.896 C 7.398 9.033,7.538 9.157,7.671 9.204 C 7.803 9.250,8.197 9.250,8.329 9.204 C 8.462 9.157,8.602 9.033,8.675 8.896 C 8.733 8.787,8.733 8.786,8.741 6.816 C 8.749 4.664,8.749 4.662,8.596 4.481 C 8.472 4.333,8.339 4.284,8.040 4.276 C 7.893 4.272,7.743 4.278,7.706 4.290 M7.786 10.530 C 7.597 10.592,7.410 10.753,7.319 10.932 C 7.249 11.072,7.237 11.325,7.294 11.495 C 7.388 11.780,7.697 12.000,8.000 12.000 C 8.303 12.000,8.612 11.780,8.706 11.495 C 8.763 11.325,8.751 11.072,8.681 10.932 C 8.616 10.804,8.460 10.646,8.333 10.580 C 8.217 10.520,7.904 10.491,7.786 10.530 "
                                    stroke="none"
                                    fillRule="evenodd"
                                  ></path>
                                </g>
                              </svg>
                              <p className="text-white/50 text-md">
                                {" "}
                                You have no Bits yet :-(
                              </p>
                            </div>
                            <span className="text-skyblue text-xs sm:text-sm font-medium">
                              Go to the Map ...
                              <br /> Find a bit you want & hit the Buy Bits
                              button
                            </span>
                          </div>
                        )}
                      </div>
                      {loggedUserPurchasedBitInfo &&
                        loggedUserPurchasedBitInfo?.length > 0 && (
                          <button
                            className="rounded-[25px] text-18px pt-2.5 pb-1 w-full bg-skyblue/20 hover:opacity-80 duration-300 mt-6"
                            title="Gift a Bit"
                            onClick={() => {
                              setGiftBitPopup(true);
                            }}
                          >
                            <span className="inline-flex justify-center gap-2">
                              <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M19.5 7.75H18.1C18.5 7.27 18.75 6.67 18.75 6C18.75 4.48 17.52 3.25 16 3.25C14.32 3.25 12.84 4.14 12 5.46C11.16 4.14 9.68 3.25 8 3.25C6.48 3.25 5.25 4.48 5.25 6C5.25 6.67 5.5 7.27 5.9 7.75H4.5C3.81 7.75 3.25 8.31 3.25 9V11.5C3.25 12.1 3.68 12.58 4.25 12.7V19.5C4.25 20.19 4.81 20.75 5.5 20.75H18.5C19.19 20.75 19.75 20.19 19.75 19.5V12.7C20.32 12.58 20.75 12.1 20.75 11.5V9C20.75 8.31 20.19 7.75 19.5 7.75ZM19.25 11.25H12.75V9.25H19.25V11.25ZM16 4.75C16.69 4.75 17.25 5.31 17.25 6C17.25 6.69 16.69 7.25 16 7.25H12.84C13.18 5.82 14.47 4.75 16 4.75ZM8 4.75C9.53 4.75 10.82 5.82 11.16 7.25H8C7.31 7.25 6.75 6.69 6.75 6C6.75 5.31 7.31 4.75 8 4.75ZM4.75 9.25H11.25V11.25H4.75V9.25ZM5.75 12.75H11.25V19.25H5.75V12.75ZM18.25 19.25H12.75V12.75H18.25V19.25Z"
                                  fill="white"
                                />
                              </svg>
                              Gift a Bit
                            </span>
                          </button>
                        )}
                      <button
                        className="rounded-[10px] text-18px py-2.5 w-full bg-RedT hover:opacity-80 duration-300 mt-6"
                        title="Log out"
                        onClick={() => {
                          setLogoutUser(true);
                          setLogedIn(false);
                        }}
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>
      <ToastContainer position="top-right" />
    </div>
  );
};

export default Header;
